/**
 * Related Post Card Component
 * Displays a single blog post preview card with image, title, and date
 * Based on Figma design specifications from relatedpostcard-output.rules
 */

import React from "react";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { format, isValid, parseISO } from "date-fns";
import styles from "./RelatedPostCard.module.css";

export interface RelatedPostCardProps {
  /**
   * The URL slug for the blog post
   */
  slug: string;
  /**
   * The title of the blog post
   */
  title: string;
  /**
   * The featured image URL for the blog post
   */
  imageUrl: string;
  /**
   * The publication date of the blog post
   */
  date?: string;
  /**
   * Alt text for the featured image
   */
  imageAlt?: string;

  summary?: string;

}

/**
 * A card component for displaying a related blog post preview
 * Includes an image, title, and formatted date
 */
const RelatedPostCard: React.FC<RelatedPostCardProps> = ({
  slug,
  title,
  imageUrl,
  date,
  imageAlt = "",
  summary
}) => {
  // Parse and validate the date
  const formattedDate = React.useMemo(() => {
    if (!date) return "";
    const parsedDate = parseISO(date);
    return isValid(parsedDate) ? format(parsedDate, "MMM dd, yyyy") : "";
  }, [date]);

  return (
    <Link 
      href={`/travel-blog/${slug}`} 
      className={styles.link}
      data-testid={`related-post-${slug}`}
      aria-label={`Read more about ${title}`}
    >
      <article 
        className={styles.card}
        data-testid={`related-post-card-${slug}`}
      >
        <div 
          className={styles.imageContainer}
          data-testid={`related-post-image-${slug}`}
        >
          <Image
            src={imageUrl}
            alt={imageAlt || title}
            fill
            sizes="297px"
            className={styles.image}
            priority={false}
          />
        </div>
        <div 
          className={styles.content}
          data-testid={`related-post-content-${slug}`}
        >
          <h3 
            className={styles.title}
            data-testid={`related-post-title-${slug}`}
          >
            {title}
          </h3>
          <h4
            className={styles.summary}
            data-testid={`related-post-summary-${slug}`}
          >
            {summary}
          </h4>
          {formattedDate && (
            <time 
              dateTime={date} 
              className={styles.date}
              data-testid={`related-post-date-${slug}`}
            >
              {formattedDate}
            </time>
          )}
        </div>
      </article>
    </Link>
  );
};

export default RelatedPostCard; 